import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useBranding } from '../context/BrandingContext';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
  Boxes, AlertTriangle, PackageX, CheckCircle2, Tag, MapPin, Layers, Plus, ArrowRight,
  Trash2, ShieldAlert, TrendingUp, Activity, Network, FileText
} from 'lucide-react';
import { openLetterheadWindow } from '../utils/brandedPrint';
import { toast } from 'sonner';

const StatCard = ({ icon: Icon, label, value, accent, testId, onClick }) => (
  <Card
    className={`industrial-card ${onClick ? 'cursor-pointer hover:border-orange-300 transition' : ''}`}
    onClick={onClick}
    data-testid={testId}
  >
    <CardContent className="p-5 flex items-center gap-4">
      <div className={`w-10 h-10 flex items-center justify-center rounded-sm ${accent}`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      <div className="min-w-0">
        <div className="text-xs uppercase tracking-wider text-slate-500">{label}</div>
        <div className="text-2xl font-bold text-slate-900 mono">{value}</div>
      </div>
    </CardContent>
  </Card>
);

const BarRow = ({ label, count, max, testId }) => (
  <div className="flex items-center gap-3" data-testid={testId}>
    <div className="text-sm text-slate-700 w-32 truncate">{label}</div>
    <div className="flex-1 h-2 bg-slate-100 rounded-sm overflow-hidden">
      <div className="h-full bg-orange-500" style={{ width: `${max ? Math.max(4, (count / max) * 100) : 0}%` }} />
    </div>
    <div className="text-sm text-slate-700 mono w-10 text-right">{count}</div>
  </div>
);

const ItemDashboardPage = () => {
  const { api } = useAuth();
  const { branding } = useBranding();
  const appName = branding?.app_name || 'BOM Maestro';
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [productRisk, setProductRisk] = useState(null);
  const [loading, setLoading] = useState(true);
  const [healthIndex, setHealthIndex] = useState(null);

  const fetchStats = async () => {
    try {
      const [s, r, hi] = await Promise.all([
        api().get('/items/dashboard/stats'),
        api().get('/items/dashboard/product-risk'),
        api().get('/reports/items/health-index').catch(() => ({ data: null })),
      ]);
      setStats(s.data);
      setProductRisk(r.data);
      setHealthIndex(hi.data);
    } catch {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  // iter40 Phase C — printable Health Index report (8.5×11)
  const printHealthIndex = async () => {
    if (!healthIndex) { toast.error('Health Index not loaded'); return; }
    const t = healthIndex.totals;
    const fmtRow = (it) => `<tr><td>${it.item_sku}</td><td>${it.item_name || ''}</td><td style="text-align:right">${it.current_quantity}</td><td style="text-align:right">${it.reorder_point}</td><td>${it.country_of_origin || '—'}</td></tr>`;
    const html = (`<!doctype html><html><head><title>Inventory Health Index</title>
<style>
  @page { size: 8.5in 11in; margin: 0.5in; }
  body { font-family: 'IBM Plex Sans', Arial, sans-serif; color: #1e293b; }
  h1 { font-size: 18px; margin: 0 0 4px; }
  .as-of { font-size: 10px; color: #64748b; margin-bottom: 12px; }
  .totals { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; margin-bottom: 16px; }
  .kpi { border: 1px solid #cbd5e1; padding: 6px 10px; border-radius: 2px; }
  .kpi .l { font-size: 9px; text-transform: uppercase; letter-spacing: 0.04em; color: #64748b; }
  .kpi .v { font-size: 20px; font-weight: 700; font-family: 'JetBrains Mono', monospace; }
  table { width: 100%; border-collapse: collapse; margin-top: 6px; font-size: 11px; }
  th { background: #f1f5f9; padding: 4px 6px; border-bottom: 1px solid #cbd5e1; text-align: left; }
  td { padding: 3px 6px; border-bottom: 1px solid #f1f5f9; }
  h2 { font-size: 13px; margin: 14px 0 4px; }
</style></head><body>
<!--LETTERHEAD-->
<div class="as-of">As of ${new Date().toLocaleString()} · ${appName}</div>
<div class="totals">
  <div class="kpi"><div class="l">All items</div><div class="v">${t.all_items}</div></div>
  <div class="kpi"><div class="l">Active</div><div class="v" style="color:#10b981">${t.active}</div></div>
  <div class="kpi"><div class="l">Healthy</div><div class="v" style="color:#10b981">${t.healthy}</div></div>
  <div class="kpi"><div class="l">Inactive</div><div class="v" style="color:#94a3b8">${t.inactive}</div></div>
  <div class="kpi"><div class="l">Low stock</div><div class="v" style="color:#f59e0b">${t.low_stock}</div></div>
  <div class="kpi"><div class="l">Out of stock</div><div class="v" style="color:#dc2626">${t.out_of_stock}</div></div>
  <div class="kpi"><div class="l">Overstock</div><div class="v" style="color:#0ea5e9">${t.overstock}</div></div>
  <div class="kpi"><div class="l">Health %</div><div class="v">${t.active ? Math.round((t.healthy / t.active) * 100) : 0}%</div></div>
</div>
<h2>Out of Stock — top ${(healthIndex.out_of_stock_top || []).length}</h2>
<table><thead><tr><th>SKU</th><th>Name</th><th style="text-align:right">Qty</th><th style="text-align:right">Reorder</th><th>COO</th></tr></thead>
<tbody>${(healthIndex.out_of_stock_top || []).map(fmtRow).join('')}</tbody></table>
<h2>Low Stock — top ${(healthIndex.low_stock_top || []).length}</h2>
<table><thead><tr><th>SKU</th><th>Name</th><th style="text-align:right">Qty</th><th style="text-align:right">Reorder</th><th>COO</th></tr></thead>
<tbody>${(healthIndex.low_stock_top || []).map(fmtRow).join('')}</tbody></table>
<script>setTimeout(()=>window.print(),300);</script>
</body></html>`);
    const win = await openLetterheadWindow(html, { title: 'Inventory Health Index', features: 'width=900,height=1100' });
    if (!win) toast.error('Pop-up blocked');
  };

  useEffect(() => { fetchStats(); /* eslint-disable-next-line */ }, []);

  if (loading || !stats) {
    return <div className="flex items-center justify-center h-96"><div className="spinner" /></div>;
  }

  const maxCat = Math.max(1, ...stats.by_category.map(c => c.count));
  const maxType = Math.max(1, ...stats.by_type.map(c => c.count));
  const maxLoc = Math.max(1, ...(stats.by_location || []).map(c => c.count));

  return (
    <div className="space-y-6 animate-slideIn" data-testid="item-dashboard-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Item Dashboard</h1>
          <p className="text-slate-500 text-sm">Inventory health at a glance</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={printHealthIndex} className="rounded-sm" data-testid="dashboard-print-health-index">
            <FileText className="w-4 h-4 mr-2" /> Print Health Index
          </Button>
          <Button onClick={() => navigate('/items')} className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="dashboard-go-to-items">
            <Plus className="w-4 h-4 mr-2" /> Go to Item Master
          </Button>
        </div>
      </div>

      {/* KPI cards — clickable filters */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon={Boxes} label="Total Items" value={stats.total_items} accent="bg-slate-700" testId="kpi-total-items"
          onClick={() => navigate('/items')} />
        <StatCard icon={CheckCircle2} label="Active" value={stats.active_items} accent="bg-emerald-600" testId="kpi-active-items"
          onClick={() => navigate('/items?filter=active')} />
        <StatCard
          icon={AlertTriangle}
          label="Low Stock"
          value={stats.low_stock_count}
          accent="bg-orange-500"
          testId="kpi-low-stock"
          onClick={() => navigate('/items?filter=low_stock')}
        />
        <StatCard icon={PackageX} label="Out of Stock" value={stats.out_of_stock_count} accent="bg-red-600" testId="kpi-out-of-stock"
          onClick={() => navigate('/items?filter=out_of_stock')} />
      </div>

      {/* Product Risk Analysis */}
      <Card className="industrial-card" data-testid="product-risk-card">
        <CardHeader className="border-b border-slate-100 pb-3 flex-row items-center justify-between flex">
          <h2 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
            <Activity className="w-4 h-4 text-orange-600" /> Product Risk Analysis
          </h2>
          {productRisk?.totals && (
            <div className="text-xs text-slate-500 mono flex gap-3">
              <span data-testid="product-risk-high">High Risk: {productRisk.totals.high_risk}</span>
              <span data-testid="product-risk-rising">Rising Cost: {productRisk.totals.rising_cost}</span>
              <span data-testid="product-risk-boms">BOMs At Risk: {productRisk.totals.boms_at_risk}</span>
            </div>
          )}
        </CardHeader>
        <CardContent className="p-0">
          {!productRisk || productRisk.items.length === 0 ? (
            <div className="p-6 text-sm text-slate-500 text-center" data-testid="product-risk-empty">
              No items to score yet. Add price points or vendor risk levels to populate this view.
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {productRisk.items.slice(0, 8).map(it => {
                const tone = it.risk_score >= 60 ? 'bg-red-50 border-red-200 text-red-700'
                          : it.risk_score >= 40 ? 'bg-orange-50 border-orange-200 text-orange-700'
                          : it.risk_score >= 20 ? 'bg-amber-50 border-amber-200 text-amber-700'
                          : 'bg-emerald-50 border-emerald-200 text-emerald-700';
                return (
                  <div key={it.item_id} className="p-4 flex items-start gap-3" data-testid={`product-risk-row-${it.item_id}`}>
                    <div className={`text-xs uppercase tracking-wider font-bold px-2 py-1 border rounded-sm shrink-0 mono ${tone}`}>
                      {it.risk_score}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <button
                          onClick={() => navigate(`/items/view/${it.item_id}`)}
                          className="font-medium text-slate-800 hover:text-orange-600 hover:underline"
                          data-testid={`product-risk-name-${it.item_id}`}
                        >
                          {it.item_name}
                        </button>
                        <span className="mono text-xs text-slate-500">{it.item_sku}</span>
                        {it.has_active_recall && (
                          <span className="text-[10px] uppercase tracking-wider text-red-700 bg-red-100 px-1.5 py-0.5 rounded-sm">RECALL</span>
                        )}
                        {it.low_stock && (
                          <span className="text-[10px] uppercase tracking-wider text-orange-700 bg-orange-100 px-1.5 py-0.5 rounded-sm">LOW STOCK</span>
                        )}
                      </div>
                      <div className="text-xs text-slate-500 flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
                        {it.vendor_name && <span>Vendor: <span className="text-slate-700">{it.vendor_name}</span> (idx {it.vendor_risk_index})</span>}
                        {it.unit_cost_change_pct != null && (
                          <span className={it.unit_cost_change_pct > 0 ? 'text-red-600' : 'text-emerald-600'}>
                            <TrendingUp className="w-3 h-3 inline mr-0.5" />Unit {it.unit_cost_change_pct > 0 ? '+' : ''}{it.unit_cost_change_pct}%
                          </span>
                        )}
                        {it.shipping_cost_change_pct != null && (
                          <span className={it.shipping_cost_change_pct > 0 ? 'text-red-600' : 'text-emerald-600'}>
                            Ship {it.shipping_cost_change_pct > 0 ? '+' : ''}{it.shipping_cost_change_pct}%
                          </span>
                        )}
                      </div>
                      {it.affected_boms.length > 0 && (
                        <div className="mt-1.5 flex items-center gap-1 flex-wrap" data-testid={`product-risk-boms-${it.item_id}`}>
                          <Network className="w-3 h-3 text-slate-400" />
                          <span className="text-[10px] uppercase tracking-wider text-slate-500">Affects:</span>
                          {it.affected_boms.slice(0, 5).map(b => (
                            <span
                              key={b.bom_id}
                              className="text-xs mono px-1.5 py-0.5 bg-slate-100 text-slate-700 rounded-sm"
                              data-testid={`product-risk-bom-${b.bom_id}`}
                            >
                              {b.bom_variant_code} v{b.bom_revision}
                            </span>
                          ))}
                          {it.affected_boms.length > 5 && (
                            <span className="text-[10px] text-slate-400">+{it.affected_boms.length - 5} more</span>
                          )}
                        </div>
                      )}
                    </div>
                    <Button
                      variant="ghost" size="sm"
                      onClick={() => navigate(`/items/view/${it.item_id}`)}
                      data-testid={`product-risk-view-${it.item_id}`}
                    >
                      <ArrowRight className="w-4 h-4" />
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Disposal follow-ups (purchaser) */}
        <Card className="industrial-card" data-testid="disposal-followups-card">
          <CardHeader className="border-b border-slate-100 pb-3 flex-row items-center justify-between flex">
            <h2 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
              <Trash2 className="w-4 h-4 text-red-600" /> Disposal Follow-ups
            </h2>
            <span className="text-xs text-slate-500 mono">{(stats.recent_disposals || []).length}</span>
          </CardHeader>
          <CardContent className="p-0">
            {(!stats.recent_disposals || stats.recent_disposals.length === 0) ? (
              <div className="p-6 text-sm text-slate-500 text-center" data-testid="disposal-followups-empty">
                No disposal events to follow up on.
              </div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {stats.recent_disposals.map(d => (
                  <li key={d.transaction_id} className="p-4 flex items-start gap-3" data-testid={`disposal-followup-${d.transaction_id}`}>
                    <div className="w-8 h-8 rounded-sm bg-red-50 text-red-600 flex items-center justify-center shrink-0">
                      <Trash2 className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline flex-wrap gap-2">
                        <span className="font-medium text-slate-800">{d.item_name || d.item_sku}</span>
                        <span className="text-xs text-slate-500 mono">−{d.quantity} {d.uom}</span>
                        {d.batch_lot && (
                          <button
                            onClick={() => navigate(`/batch-lot-recall?lot=${encodeURIComponent(d.batch_lot)}`)}
                            className="text-xs text-slate-700 mono px-1.5 py-0.5 bg-slate-100 hover:bg-orange-100 hover:text-orange-700 rounded-sm"
                            data-testid={`disposal-lot-${d.transaction_id}`}
                          >
                            Lot: {d.batch_lot}
                          </button>
                        )}
                      </div>
                      <div className="text-xs text-slate-500">
                        {d.timestamp ? new Date(d.timestamp).toLocaleString() : ''}
                        {d.notes && <span className="ml-2 text-amber-700">{d.notes}</span>}
                      </div>
                    </div>
                    <Button
                      variant="ghost" size="sm"
                      onClick={() => d.item_id && navigate(`/items/view/${d.item_id}`)}
                      data-testid={`disposal-view-${d.transaction_id}`}
                    >
                      <ArrowRight className="w-4 h-4" />
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        {/* Active recalls */}
        <Card className="industrial-card" data-testid="active-recalls-card">
          <CardHeader className="border-b border-slate-100 pb-3 flex-row items-center justify-between flex">
            <h2 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
              <ShieldAlert className="w-4 h-4 text-red-600" /> Active Recalls
            </h2>
            <Button
              variant="outline" size="sm"
              onClick={() => navigate('/batch-lot-recall')}
              className="rounded-sm"
              data-testid="open-lot-recall-page"
            >
              Manage
            </Button>
          </CardHeader>
          <CardContent className="p-0">
            {(!stats.active_recalls || stats.active_recalls.length === 0) ? (
              <div className="p-6 text-sm text-slate-500 text-center" data-testid="active-recalls-dashboard-empty">
                No active lot recalls.
              </div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {stats.active_recalls.map(r => (
                  <li key={r.id} className="p-4 flex items-start gap-3" data-testid={`dashboard-recall-${r.id}`}>
                    <ShieldAlert className="w-5 h-5 text-red-600 mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <button
                          onClick={() => navigate(`/batch-lot-recall?lot=${encodeURIComponent(r.lot)}`)}
                          className="font-bold mono text-slate-900 hover:text-red-600"
                        >
                          {r.lot}
                        </button>
                        <span className="text-xs text-slate-500">{r.affected_skus?.length || 0} SKUs</span>
                      </div>
                      <div className="text-sm text-slate-700">{r.reason}</div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>


      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Low stock list */}
        <Card className="industrial-card lg:col-span-2" id="low-stock-table">
          <CardHeader className="border-b border-slate-100 pb-3 flex-row items-center justify-between flex">
            <h2 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-orange-500" /> Low Stock Alerts
            </h2>
            <span className="text-xs text-slate-500 mono">{stats.low_stock_items.length} item(s)</span>
          </CardHeader>
          <CardContent className="p-0">
            {stats.low_stock_items.length === 0 ? (
              <div className="p-8 text-center text-sm text-slate-500" data-testid="low-stock-empty">
                <CheckCircle2 className="w-8 h-8 mx-auto mb-2 text-emerald-500" />
                Everything is above reorder point.
              </div>
            ) : (
              <Table className="data-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>SKU</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead className="text-right">On Hand</TableHead>
                    <TableHead className="text-right">Reorder</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {stats.low_stock_items.map(it => (
                    <TableRow key={it.id} data-testid={`low-stock-row-${it.id}`}>
                      <TableCell className="mono font-medium">{it.item_sku}</TableCell>
                      <TableCell>{it.item_name}</TableCell>
                      <TableCell className="text-right mono text-red-600 font-semibold">
                        {it.current_quantity} {it.base_uom}
                      </TableCell>
                      <TableCell className="text-right mono text-slate-500">
                        {it.reorder_point} {it.base_uom}
                      </TableCell>
                      <TableCell>
                        <Button variant="ghost" size="sm" onClick={() => navigate(`/items/view/${it.id}`)} data-testid={`low-stock-view-${it.id}`}>
                          <ArrowRight className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Recent items */}
        <Card className="industrial-card">
          <CardHeader className="border-b border-slate-100 pb-3">
            <h2 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
              <Layers className="w-4 h-4" /> Recently Added
            </h2>
          </CardHeader>
          <CardContent className="p-0">
            {stats.recent_items.length === 0 ? (
              <div className="p-6 text-sm text-slate-500 text-center">No items yet.</div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {stats.recent_items.map(it => (
                  <li key={it.id} className="px-4 py-2.5 hover:bg-slate-50">
                    <button
                      className="w-full text-left"
                      onClick={() => navigate(`/items/view/${it.id}`)}
                      data-testid={`recent-item-${it.id}`}
                    >
                      <div className="text-sm font-medium text-slate-800 truncate">{it.item_name}</div>
                      <div className="text-xs text-slate-500 mono truncate">{it.item_sku}</div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* By category */}
        <Card className="industrial-card">
          <CardHeader className="border-b border-slate-100 pb-3">
            <h2 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
              <Tag className="w-4 h-4" /> By Category
            </h2>
          </CardHeader>
          <CardContent className="p-5 space-y-3" data-testid="dashboard-by-category">
            {stats.by_category.length === 0 && <div className="text-sm text-slate-500">No data.</div>}
            {stats.by_category.map(c => (
              <BarRow key={c.category} label={c.category} count={c.count} max={maxCat} testId={`bar-cat-${c.category}`} />
            ))}
          </CardContent>
        </Card>

        {/* By type */}
        <Card className="industrial-card">
          <CardHeader className="border-b border-slate-100 pb-3">
            <h2 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
              <Boxes className="w-4 h-4" /> By Type
            </h2>
          </CardHeader>
          <CardContent className="p-5 space-y-3" data-testid="dashboard-by-type">
            {stats.by_type.length === 0 && <div className="text-sm text-slate-500">No data.</div>}
            {stats.by_type.map(c => (
              <BarRow key={c.type} label={c.type} count={c.count} max={maxType} testId={`bar-type-${c.type}`} />
            ))}
          </CardContent>
        </Card>

        {/* By location */}
        <Card className="industrial-card">
          <CardHeader className="border-b border-slate-100 pb-3">
            <h2 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
              <MapPin className="w-4 h-4" /> By Location
            </h2>
          </CardHeader>
          <CardContent className="p-5 space-y-3" data-testid="dashboard-by-location">
            {(!stats.by_location || stats.by_location.length === 0) ? (
              <div className="text-sm text-slate-500">No items have a location assigned yet.</div>
            ) : (
              stats.by_location.map(c => (
                <BarRow key={c.location} label={c.location} count={c.count} max={maxLoc} testId={`bar-loc-${c.location}`} />
              ))
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ItemDashboardPage;
